import React, { useState, useEffect } from 'react';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Divider from '@material-ui/core/Divider';
import Snackbar from '@material-ui/core/Snackbar';
import CircularProgress from '@material-ui/core/CircularProgress';
import PrintIcon from '@material-ui/icons/Print';
import PaymentIcon from '@material-ui/icons/Payment';
import ReceiptIcon from '@material-ui/icons/Receipt';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { makeStyles } from '@material-ui/core/styles';
import { surgeryService } from '../../../services/surgeryService';
import { PAY_STATUS } from '../shared/constants';

const BILL_STATUS_MAP = {
  unpaid: { label: 'Unpaid', color: '#F44336', bg: '#FFEBEE' },
  partially_paid: { label: 'Partially Paid', color: '#FF9800', bg: '#FFF8E1' },
  paid: { label: 'Paid', color: '#4CAF50', bg: '#E8F5E9' },
  insurance: { label: 'Insurance', color: '#2196F3', bg: '#E3F2FD' },
};

const CATEGORY_GROUPS = {
  Theatre: ['Theatre fee', 'Theatre supplies'],
  Surgeon: ['Surgeon fee'],
  Anaesthesia: ['Anaesthesia fee', 'Anaesthesia drugs'],
  Consumables: ['Consumables', 'Consumables (estimated)', 'Surgical consumables'],
  Ward: ['Ward stay', 'Ward charges'],
};


const useStyles = makeStyles((theme) => ({
  root: {
    margin: 22,
    padding: 20,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  title: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#333',
  },
  statusChip: {
    fontWeight: 600,
    fontSize: '0.8rem',
  },
  surgeryInfo: {
    display: 'flex',
    gap: 24,
    marginBottom: 20,
    fontSize: '0.85rem',
    color: '#666',
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  infoLabel: {
    fontWeight: 500,
    color: '#777',
    fontSize: '0.85rem',
  },
  infoValue: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.85rem',
  },
  sectionTitle: {
    fontSize: '1.05rem',
    fontWeight: 600,
    color: '#333',
    marginTop: 24,
    marginBottom: 12,
  },
  table: {
    marginBottom: 8,
  },
  tableHead: {
    backgroundColor: '#F5F5F5',
  },
  headCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#555',
  },
  bodyCell: {
    fontSize: '0.85rem',
    color: '#333',
  },
  amountCell: {
    fontSize: '0.85rem',
    fontFamily: 'monospace',
  },
  paidGreen: {
    color: '#2E7D32',
  },
  unpaidRed: {
    color: '#C62828',
  },
  partialYellow: {
    color: '#E65100',
  },
  categoryRow: {
    backgroundColor: '#FAFAFA',
  },
  categoryCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#555',
    paddingTop: 12,
    paddingBottom: 4,
  },
  subtotalRow: {
    backgroundColor: '#F5F5F5',
  },
  subtotalCell: {
    fontWeight: 600,
    fontSize: '0.85rem',
    color: '#555',
  },
  summarySection: {
    padding: '16px 20px',
    borderRadius: 8,
    border: '1px solid #E0E0E0',
    marginTop: 16,
    marginBottom: 16,
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 0',
  },
  summaryLabel: {
    fontSize: '0.9rem',
    color: '#555',
    fontWeight: 500,
  },
  summaryValue: {
    fontSize: '0.9rem',
    color: '#333',
    fontWeight: 600,
    fontFamily: 'monospace',
  },
  outstandingBalance: {
    fontSize: '1.3rem',
    fontWeight: 700,
    fontFamily: 'monospace',
  },
  insuranceSection: {
    padding: '16px 20px',
    borderRadius: 8,
    border: '1px solid #BBDEFB',
    backgroundColor: '#F5F9FF',
    marginBottom: 16,
  },
  timelineItem: {
    display: 'flex',
    gap: 12,
    padding: '10px 0',
    borderBottom: '1px solid #F0F0F0',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  timelineDot: {
    fontSize: '0.7rem',
    color: '#4CAF50',
    marginTop: 3,
  },
  timelineContent: {
    flex: 1,
  },
  timelineDate: {
    fontSize: '0.78rem',
    color: '#999',
  },
  timelineAmount: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: '#333',
    fontFamily: 'monospace',
  },
  timelineDetail: {
    fontSize: '0.8rem',
    color: '#666',
  },
  actions: {
    display: 'flex',
    gap: 12,
    marginTop: 20,
  },
  actionBtn: {
    textTransform: 'none',
    fontWeight: 500,
  },
  loadingWrapper: {
    display: 'flex',
    justifyContent: 'center',
    padding: 80,
  },
  errorText: {
    textAlign: 'center',
    padding: 40,
    color: '#F44336',
  },
}));

const getRowPayClass = (item, classes) => {
  if (!item) return '';
  if (item.paid >= item.amount) return classes.paidGreen;
  if (item.paid > 0) return classes.partialYellow;
  return classes.unpaidRed;
};

const categorizeItems = (items) => {
  const categorized = {};
  const uncategorized = [];

  items.forEach((item) => {
    let placed = false;
    for (const [category, keywords] of Object.entries(CATEGORY_GROUPS)) {
      if (keywords.some((kw) => item.description.toLowerCase().includes(kw.toLowerCase()))) {
        if (!categorized[category]) categorized[category] = [];
        categorized[category].push(item);
        placed = true;
        break;
      }
    }
    if (!placed) uncategorized.push(item);
  });

  if (uncategorized.length > 0) {
    categorized['Other'] = uncategorized;
  }

  return categorized;
};

const SurgeryBillSummary = ({ surgeryId }) => {
  const classes = useStyles();

  const [bill, setBill] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  useEffect(() => {
    let cancelled = false;
    const fetchBill = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await surgeryService.getBillSummary(surgeryId);
        const data = res?.data || res;
        if (!cancelled) setBill(data);
      } catch (err) {
        console.error('Failed to load bill summary:', err);
        if (!cancelled) setError('Failed to load bill summary. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchBill();
    return () => { cancelled = true; };
  }, [surgeryId]);

  const handleRecordPayment = () => {
    setSnackbar({ open: true, message: 'Opening payment recording form...' });
  };

  const handlePrint = () => {
    setSnackbar({ open: true, message: 'Preparing bill for printing...' });
  };

  const handleGenerateReceipt = () => {
    setSnackbar({ open: true, message: 'Generating receipt...' });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ open: false, message: '' });
  };

  if (loading) {
    return (
      <Paper className={classes.root} elevation={1}>
        <div className={classes.loadingWrapper}>
          <CircularProgress size={32} />
        </div>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper className={classes.root} elevation={1}>
        <Typography className={classes.errorText}>{error}</Typography>
      </Paper>
    );
  }

  const items = bill?.items || [];
  const total = bill?.total || 0;
  const paid = bill?.paid || 0;
  const balance = bill?.balance || 0;
  const currency = bill?.currency || 'UGX';
  const billStatus = bill?.status || 'unpaid';
  const statusInfo = BILL_STATUS_MAP[billStatus] || BILL_STATUS_MAP.unpaid;

  const categorized = categorizeItems(items);
  const hasInsurance = billStatus === 'insurance' || paid > 0;

  return (
    <Paper className={classes.root} elevation={1}>
      {/* Header */}
      <div className={classes.header}>
        <Typography className={classes.title}>Surgery Bill Summary</Typography>
        <Chip
          label={statusInfo.label}
          className={classes.statusChip}
          style={{ backgroundColor: statusInfo.bg, color: statusInfo.color }}
        />
      </div>

      {/* Surgery Info */}
      <div className={classes.surgeryInfo}>
        <div className={classes.infoItem}>
          <Typography className={classes.infoLabel}>Surgery ID:</Typography>
          <Typography className={classes.infoValue}>{bill?.surgery_id || surgeryId}</Typography>
        </div>
        <div className={classes.infoItem}>
          <Typography className={classes.infoLabel}>Patient:</Typography>
          <Typography className={classes.infoValue}>Patient record linked to surgery</Typography>
        </div>
      </div>

      <Divider />

      {/* Itemized Table with Category Grouping */}
      <Typography className={classes.sectionTitle}>Itemized Charges</Typography>
      <Table className={classes.table} size="small">
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell className={classes.headCell}>Description</TableCell>
            <TableCell className={classes.headCell} align="right">Charged ({currency})</TableCell>
            <TableCell className={classes.headCell} align="right">Paid ({currency})</TableCell>
            <TableCell className={classes.headCell} align="right">Balance ({currency})</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(categorized).map(([category, catItems]) => {
            const catTotal = catItems.reduce((s, i) => s + (i.amount || 0), 0);
            const catPaid = catItems.reduce((s, i) => s + (i.paid || 0), 0);
            const catBalance = catTotal - catPaid;

            return (
              <React.Fragment key={category}>
                <TableRow className={classes.categoryRow}>
                  <TableCell className={classes.categoryCell} colSpan={4}>
                    {category}
                  </TableCell>
                </TableRow>
                {catItems.map((item, idx) => {
                  const itemBalance = (item.amount || 0) - (item.paid || 0);
                  const payClass = getRowPayClass(item, classes);
                  return (
                    <TableRow key={idx}>
                      <TableCell className={classes.bodyCell} style={{ paddingLeft: 24 }}>
                        {item.description}
                      </TableCell>
                      <TableCell className={classes.amountCell} align="right">
                        {(item.amount || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className={`${classes.amountCell} ${payClass}`} align="right">
                        {(item.paid || 0).toLocaleString()}
                      </TableCell>
                      <TableCell
                        className={classes.amountCell}
                        align="right"
                        style={{ color: itemBalance > 0 ? '#C62828' : '#2E7D32' }}
                      >
                        {itemBalance.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className={classes.subtotalRow}>
                  <TableCell className={classes.subtotalCell} style={{ paddingLeft: 24 }}>
                    {category} Subtotal
                  </TableCell>
                  <TableCell className={classes.subtotalCell} align="right" style={{ fontFamily: 'monospace' }}>
                    {catTotal.toLocaleString()}
                  </TableCell>
                  <TableCell className={classes.subtotalCell} align="right" style={{ fontFamily: 'monospace' }}>
                    {catPaid.toLocaleString()}
                  </TableCell>
                  <TableCell
                    className={classes.subtotalCell}
                    align="right"
                    style={{ fontFamily: 'monospace', color: catBalance > 0 ? '#C62828' : '#2E7D32' }}
                  >
                    {catBalance.toLocaleString()}
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>

      {/* Summary Section */}
      <div className={classes.summarySection}>
        <div className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>Total Charged</Typography>
          <Typography className={classes.summaryValue}>
            {currency} {total.toLocaleString()}
          </Typography>
        </div>
        <div className={classes.summaryRow}>
          <Typography className={classes.summaryLabel}>Total Paid</Typography>
          <Typography className={classes.summaryValue} style={{ color: '#2E7D32' }}>
            {currency} {paid.toLocaleString()}
          </Typography>
        </div>
        <Divider style={{ margin: '8px 0' }} />
        <div className={classes.summaryRow}>
          <Typography className={classes.summaryLabel} style={{ fontSize: '1rem', fontWeight: 600 }}>
            Outstanding Balance
          </Typography>
          <Typography
            className={classes.outstandingBalance}
            style={{ color: balance > 0 ? '#C62828' : '#2E7D32' }}
          >
            {currency} {balance.toLocaleString()}
          </Typography>
        </div>
      </div>

      {/* Insurance Section */}
      {bill?.insurance && (
        <>
          <Typography className={classes.sectionTitle}>Insurance Coverage</Typography>
          <div className={classes.insuranceSection}>
            {bill.insurance.claimed != null && (
              <div className={classes.summaryRow}>
                <Typography className={classes.summaryLabel}>Amount Claimed</Typography>
                <Typography className={classes.summaryValue}>
                  {currency} {Number(bill.insurance.claimed).toLocaleString()}
                </Typography>
              </div>
            )}
            {bill.insurance.approved != null && (
              <div className={classes.summaryRow}>
                <Typography className={classes.summaryLabel}>Approved Amount</Typography>
                <Typography className={classes.summaryValue} style={{ color: '#2E7D32' }}>
                  {currency} {Number(bill.insurance.approved).toLocaleString()}
                </Typography>
              </div>
            )}
            {bill.insurance.copay_percentage != null && (
              <div className={classes.summaryRow}>
                <Typography className={classes.summaryLabel}>
                  Co-Pay ({bill.insurance.copay_percentage}%)
                </Typography>
                <Typography className={classes.summaryValue}>
                  {currency} {Number(bill.insurance.copay_amount || 0).toLocaleString()}
                </Typography>
              </div>
            )}
            <Divider style={{ margin: '8px 0' }} />
            {bill.insurance.patient_liability != null && (
              <div className={classes.summaryRow}>
                <Typography className={classes.summaryLabel} style={{ fontWeight: 600 }}>
                  Patient Liability
                </Typography>
                <Typography className={classes.summaryValue} style={{ color: '#C62828', fontWeight: 700 }}>
                  {currency} {Number(bill.insurance.patient_liability).toLocaleString()}
                </Typography>
              </div>
            )}
          </div>
        </>
      )}

      {/* Payment History Timeline */}
      <Typography className={classes.sectionTitle}>Payment History</Typography>
      {(bill?.payment_history || []).length === 0 ? (
        <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic', marginBottom: 16 }}>
          No payment history recorded
        </Typography>
      ) : (
        (bill.payment_history).map((pmt, idx) => (
          <div key={pmt.id || idx} className={classes.timelineItem}>
            <FiberManualRecordIcon className={classes.timelineDot} />
            <div className={classes.timelineContent}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography className={classes.timelineAmount}>
                  {currency} {Number(pmt.amount).toLocaleString()}
                </Typography>
                <Typography className={classes.timelineDate}>{pmt.date}</Typography>
              </div>
              <Typography className={classes.timelineDetail}>
                {pmt.method} &middot; Ref: {pmt.reference}
              </Typography>
              <Typography className={classes.timelineDetail}>{pmt.recorded_by}</Typography>
            </div>
          </div>
        ))
      )}

      {/* Action Buttons */}
      <div className={classes.actions}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<PaymentIcon />}
          className={classes.actionBtn}
          onClick={handleRecordPayment}
        >
          Record Payment
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<PrintIcon />}
          className={classes.actionBtn}
          onClick={handlePrint}
        >
          Print Bill
        </Button>
        <Button
          variant="outlined"
          color="default"
          startIcon={<ReceiptIcon />}
          className={classes.actionBtn}
          onClick={handleGenerateReceipt}
        >
          Generate Receipt
        </Button>
      </div>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default SurgeryBillSummary;
